import dayjs from 'dayjs'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import { IClientSetup } from 'src/app/shared/models/clientSetup.model'
import * as yup from 'yup'

function getCutoffTimeList(minuteRange: number) {
  const times = []
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += minuteRange) {
      // Pad hours and minutes with zeros for consistent formatting
      const hourString = hour.toString().padStart(2, '0')
      const minuteString = minute.toString().padStart(2, '0')
      const timeString = `${hourString}:${minuteString}:00`

      // Convert to 12-hour format and determine AM/PM
      const hour12 = hour % 12 || 12 // Handle 0 hour as 12 AM
      const amPm = hour < 12 ? 'AM' : 'PM'
      const formattedTime = `${hour12}:${minuteString} ${amPm}`

      times.push({
        value: timeString,
        label: formattedTime,
      })
    }
  }
  return times
}

function generateValidationSchema(t: any) {
  const validationSchema = yup
    .object()
    .shape({
      clientName: yup
        .string()
        .required(t('SystemMessages:' + SYSTEM_MESSAGE_IDS.BLANK_CLIENT_NAME)),

      clientNumber: yup
        .string()
        .required(t('ClientSetup:addNewClient.validation.clientIdRequired')),

      // exceptionReminderEmailTimeInMinutes, defaultCheckExceptionCutoffTime and defaultAchExceptionCutoffTime
      // are now managed globally via System-Wide Rules and shown view-only, so they are intentionally NOT
      // validated here (they are not user-editable fields anymore).

      defaultCheckExceptionCreationType: yup.string(),

      issuedCheckFileFormats: yup.array().when('defaultCheckExceptionCreationType', (val) => {
        // if (val[0] === 'CREATE') {
        //     return yup.array().min(
        //         1,
        //         t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
        //             Required_Field: `${t('ClientSetup:addNewClient.label.issuedCheckFileFormats')}`,
        //         }),
        //     )
        // }
        return yup.array().notRequired()
      }),

      autoLoadIssuedCheckFiles: yup.boolean(),

      sftpFileLocation: yup.string().when('autoLoadIssuedCheckFiles', (val) => {
        if (val[0]) {
          return yup
            .string()
            .required(
              t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
                Required_Field: `SFTP File Location`,
              }),
            )
        }
        return yup.string().notRequired()
      }),

      payeeMatch: yup.boolean(),

      payeeMatchParameterId: yup.string().when('payeeMatch', (val) => {
        if (val[0]) {
          return yup
            .string()
            .required(
              t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
                Required_Field: `Payee Match Parameter`,
              }),
            )
        }
        return yup.string().notRequired()
      }),

      activationDate: yup
        .string()
        .nullable()
        .notRequired()
        .test('is-valid-date-or-empty', 'Invalid Date', (value) => {
          if (!value) return true
          return dayjs(value).isValid()
        })
        .test('is-not-past', 'The date cannot be earlier than today.', (value) => {
          if (!value) return true
          const date = dayjs(value)
          return date.isAfter(dayjs(), 'day')
        }),

      defaultAchExceptionCreationType: yup.string().required(),
    })
    .test(
      'at-least-one-exception-create',
      'At least one exception type (ACH or Check) must be enabled to proceed.',
      function (values) {
        const { defaultAchExceptionCreationType, defaultCheckExceptionCreationType } = values || {}

        const isAchNoCreate = defaultAchExceptionCreationType === 'NOCREATE'
        const isCheckNoCreate = defaultCheckExceptionCreationType === 'NOCREATE'

        return !(isAchNoCreate && isCheckNoCreate)
      },
    )

  return validationSchema
}


const formatDataBeforeSubmit = (data: IClientSetup) => {
  // The 3 exception fields (reminder + cutoff times) are managed globally via System-Wide Rules and shown
  // view-only. They are not user-editable, but the current global values already loaded into the form are
  // still sent so a new Client is created carrying the current global values and the payload stays valid.
  return {
    ...data,
    clientName: data?.clientName.trim(),
    referenceCode: data?.referenceCode.trim(),
    activationDate: data?.activationDate ? dayjs(data?.activationDate).format('YYYY-MM-DD') : null,
    archived: (!data?.id && !!data?.activationDate) || data?.archived
  }
}

const RESET_FORM_FIELDS = {
  CHECK_EXCEPTIONS: [
    'defaultCheckDecision',
    'defaultCheckReason',
    'issuedCheckFileFormats',
    'displayIssuedPayee',
    'payeeMatch',
    'payeeMatchParameterId',
    'displayNotes',
    'autoLoadIssuedCheckFiles',
    'sftpFileLocation',
  ],
  ACH_CHECK_CHECK_EXCEPTIONS: ['defaultAchDecision', 'defaultAchReason', 'achAuthorizationRulesNotificationType'],
}

export { formatDataBeforeSubmit, generateValidationSchema, getCutoffTimeList, RESET_FORM_FIELDS }
